/**
 * Snow particle field. fillRect per particle (cheaper than arc).
 * Sine sway on X, linear fall on Y, wraps around edges.
 */

interface Flake {
  x: number
  y: number
  r: number
  vy: number
  vx: number
  phase: number
}

export class Snow {
  private flakes: Flake[] = []
  private w = 0
  private h = 0
  private count: number

  constructor(count: number) {
    this.count = count
  }

  resize(w: number, h: number): void {
    this.w = w
    this.h = h
    if (this.flakes.length === 0) {
      this.spawn()
    }
  }

  setCount(count: number): void {
    this.count = count
    this.spawn()
  }

  private spawn(): void {
    this.flakes = []
    for (let i = 0; i < this.count; i++) {
      this.flakes.push(this.makeFlake(Math.random() * this.h))
    }
  }

  private makeFlake(yStart: number): Flake {
    const r = 0.6 + Math.random() * 2.2
    return {
      x: Math.random() * this.w,
      y: yStart,
      r,
      vy: 0.3 + r * 0.4 + Math.random() * 0.4,
      vx: (Math.random() - 0.5) * 0.4,
      phase: Math.random() * Math.PI * 2,
    }
  }

  tick(dt: number): void {
    const step = dt / 16.666
    for (const f of this.flakes) {
      f.phase += 0.01 * step
      f.x += (f.vx + Math.sin(f.phase) * 0.35) * step
      f.y += f.vy * step
      if (f.y > this.h + 8) {
        f.y = -8
        f.x = Math.random() * this.w
      }
      if (f.x < -8) f.x = this.w + 8
      else if (f.x > this.w + 8) f.x = -8
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    ctx.save()
    for (const f of this.flakes) {
      // Gradient-like: bigger flakes get a soft halo
      const alpha = 0.35 + f.r * 0.16
      ctx.globalAlpha = alpha
      ctx.fillStyle = '#ffffff'
      ctx.beginPath()
      ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2)
      ctx.fill()
      // Soft blue tint halo
      ctx.globalAlpha = alpha * 0.5
      ctx.fillStyle = '#b8dcf0'
      ctx.beginPath()
      ctx.arc(f.x, f.y, f.r * 1.8, 0, Math.PI * 2)
      ctx.fill()
    }
    ctx.globalAlpha = 1
    ctx.restore()
  }
}
